/**
 * The emoji that are ours.
 *
 * Everything in `emoji.ts` is Unicode: a character that every machine already
 * has a picture for, which is why a message is a string and nothing in the
 * protocol has to know what a 🎉 is. This file is the other half — the
 * seventy things a room about this product says that Unicode has no
 * character for. There is no code point for a broken build, for the third
 * rebase of the afternoon, for a tower of technical debt, or for the specific
 * feeling of being paged at 03:00, and a learner who has just watched their
 * tests go green wants to say so with something better than a green square.
 *
 * Four groups, and the split is what each one is for. **EngineerOS** is the
 * work itself — the commands, the states a repository can be in, the machine.
 * **Folklore** is what engineers say to each other about the work, which is
 * mostly jokes that are older than anyone here: the rubber duck, the yak, the
 * bikeshed, the bug that vanishes when you look at it, the vim session nobody
 * has escaped. **Expressions** is the face you make. A learner arrives knowing
 * none of the folklore and leaves fluent in it, which is most of what being in
 * this trade feels like from the inside, so the palette is somewhere they meet
 * it rather than a thing they are taught. **Stickers** are the fourth, and the
 * only thing that makes one a sticker is how big it is drawn — see the group
 * itself, at the foot of the table.
 *
 * They travel as their own shortcode and nothing else. `:shipit:` goes on the
 * wire as the eleven characters `:shipit:`, is stored as those characters, and
 * is drawn as a picture only by a tab that knows the name — which is the same
 * bargain Slack made, and it is the right one: the Worker keeps a validator
 * that only has to know about text, an old tab renders a word rather than a
 * hollow box, and a message copied out of the room pastes as something a human
 * can still read. `expandShortcodes` leaves these alone for exactly that
 * reason: they are not shorthand for a character, they *are* the character.
 *
 * The drawings live in the tab (`web/src/components/chat/CustomEmoji.tsx`),
 * because they are SVG and the Worker has no use for them. This file is the
 * roster both ends agree on: the name, what it is called in English, and the
 * words someone might search for. A drawing without a row here cannot be
 * picked; a row here without a drawing renders as its own shortcode, which is
 * a legible failure rather than a broken one.
 *
 * Each row is `code | Name | search words | الاسم`. Codes are lowercase, and
 * must not collide with a Unicode shortcode in `emoji.ts` — the protocol test
 * asserts it, because a collision would mean a `:hotfix:` that expands to a
 * plaster on one build and draws our own on the next.
 *
 * ## The fourth column, and the Arabic in the third
 *
 * The name is the only part of a row anybody reads, so it is the only part
 * with two versions. The code is typed between two colons and the search words
 * are typed into a box; both keep their Latin spelling in both languages, and
 * the Arabic words are *appended* to the same list rather than replacing
 * anything — an Arabic learner types `تعارض` and an English one types
 * `conflict`, and the same cell comes back. `keys[0]` is still the shortcode,
 * which the protocol test pins.
 *
 * Most of Folklore is a trade idiom, and a literal Arabic rendering of "yak
 * shaving" is not a joke, it is a sentence about an animal. So the Arabic
 * names translate the *sense* — `yakshave` is "a task that drags another one
 * behind it" — except where Arabic-speaking engineers already say the English
 * term out loud. `bus factor`, `off-by-one`, `deadlock`, `race condition`,
 * `heisenbug` and `memory leak` are said in English in an Arabic sentence, so
 * the name keeps the term and the Arabic glosses it. The drawing carries the
 * joke either way; the name only has to point at it.
 */
import { emojiOnly } from './emoji.js';
/** One emoji's name in one language. The twin of `groupName` in emoji.ts. */
export function customName(emoji, locale = 'en') {
    return locale === 'ar' ? emoji.nameAr : emoji.name;
}
/* ── The roster ─────────────────────────────────────────────────────────── */
const TABLE = [
    [
        'EngineerOS',
        // The product's own name, in both languages. It is a proper noun
        // (docs/i18n.md §2) and there is nothing in it to translate.
        'EngineerOS',
        [
            'shipit       | Ship it             | ship deploy release go crate send شحن نشر أطلق صندوق | اشحنها',
            'deploying    | Deploying           | deploy rollout shipping progress release نشر إطلاق جارٍ | جارٍ النشر',
            'greenbuild   | Green build         | ci pass passing build green tests suite بناء ناجح أخضر اختبارات | بناء ناجح',
            'redbuild     | Broken build        | ci fail failing red broken build tests بناء فاشل أحمر معطل | بناء فاشل',
            'merged       | Merged              | merge merged branch pr landed دمج مدموج فرع | مدموج',
            'cherrypick   | Cherry-pick         | cherry pick cherrypick commit backport انتقاء نقل | cherry-pick',
            'stash        | Stashed             | stash shelf later put_away wip تخزين جانبًا لاحقًا | محفوظ في stash',
            'bisect       | Bisecting           | bisect find culprit search halve تنصيف بحث تقسيم | جارٍ bisect',
            'squash       | Squashed            | squash fixup tidy one_commit history ضغط دمج ترتيب | squash في commit واحد',
            'conflict     | Merge conflict      | conflict merge rebase stuck markers تعارض دمج عالق | تعارض دمج',
            'forcepush    | Force push          | force push rewrite history danger قوة خطر إعادة كتابة | push بالقوة',
            'rollback     | Rollback            | rollback revert undo back previous استرجاع تراجع رجوع | استرجاع',
            'hotfix       | Hotfix              | hotfix patch fix production urgent إصلاح عاجل رقعة إنتاج | إصلاح عاجل',
            'flaky        | Flaky test          | flaky test intermittent retry sometimes اختبار متذبذب متقطع | اختبار متذبذب',
            'lgtm         | LGTM                | lgtm approve review ship stamp good موافق مراجعة ختم | LGTM — موافق',
            'nit          | Nit                 | nit nitpick small review comment minor ملاحظة صغيرة مراجعة | ملاحظة صغيرة',
            'wip          | Work in progress    | wip draft unfinished later progress عمل جارٍ مسودة | عمل جارٍ',
            'prompt       | The prompt          | prompt shell terminal caret engineeros محث طرفية صدفة | محث الأوامر',
            'sudo         | sudo                | sudo root admin permission superuser جذر صلاحية مدير | sudo',
            'thebox       | The box             | vm machine emulator box linux booted صندوق آلة جهاز | الصندوق',
            'localhost    | Localhost           | localhost local home 127 dev mine محلي جهازي | localhost',
            'pager        | Paged               | pager oncall incident page 3am woken مناوبة نداء حادثة | نداء المناوبة',
            'shipfriday   | Friday deploy       | friday deploy weekend risky brave جمعة نشر مخاطرة | نشر يوم الجمعة',
            'levelup      | Level up            | level up rank promoted progress next مستوى ترقية رتبة | مستوى أعلى',
        ],
    ],
    [
        'Folklore',
        'حكايات المهنة',
        [
            'rubberduck   | Rubber duck         | duck debug rubberduck explain talking بطة شرح تصحيح | البطة المطاطية',
            'yakshave     | Yak shaving         | yak shave sidetrack detour tangent انحراف تشعب مهمة | مهمة تجرّ أخرى',
            'bikeshed     | Bikeshedding        | bikeshed trivial argument shed colour جدال تفاهات لون | جدال حول التفاهات',
            'worksonmine  | Works on my machine | womm works machine mine excuse local جهازي تعمل عذر | تعمل على جهازي',
            'spaghetti    | Spaghetti code      | spaghetti tangle mess coupled legacy شيفرة متشابكة فوضى | شيفرة متشابكة',
            'offbyone     | Off by one          | off_by_one fencepost index loop count خطأ فهرس عد واحد | off-by-one — خطأ بمقدار واحد',
            'heisenbug    | Heisenbug           | heisenbug vanishes observe debugger gone خلل يختفي مراقبة | heisenbug — خلل يختفي عند مراقبته',
            'memoryleak   | Memory leak         | memory leak heap ram grows oom تسريب ذاكرة | memory leak — تسريب ذاكرة',
            'racecondition| Race condition      | race condition timing concurrent flaky تسابق توقيت تزامن | race condition — تسابق',
            'deadlock     | Deadlock            | deadlock stuck waiting lock hang تجمد عالق انتظار قفل | deadlock — تجمّد متبادل',
            'techdebt     | Technical debt      | tech debt shortcut later interest tower دين تقني اختصار | دين تقني',
            'busfactor    | Bus factor          | bus factor only_person knowledge risk معرفة خطر شخص | bus factor — شخص واحد يعرف كل شيء',
            'cowboycode   | Cowboy coding       | cowboy yolo straight_to_prod no_review تهور بلا مراجعة | برمجة بلا مراجعة',
            'exitvim      | Cannot exit vim     | vim exit quit q editor help خروج محرر عالق | لا أستطيع الخروج من vim',
            'printf       | printf debugging    | printf print log console debug here طباعة تتبع سجل | تتبّع بـ printf',
            'tabsvsspaces | Tabs or spaces      | tabs spaces indent holy_war style مسافات إزاحة جدال | tabs أم مسافات',
        ],
    ],
    [
        'Expressions',
        'تعابير',
        [
            'thisisfine   | This is fine        | fine fire burning calm ok denial حريق هدوء إنكار | كل شيء بخير',
            'panic        | Kernel panic        | panic crash dead scream oh_no انهيار نواة ذعر | kernel panic',
            'stare        | Long stare          | stare blank speechless what silence تحديق صمت ذهول | تحديق طويل',
            'sweating     | Sweating            | sweat nervous worried close friday عرق توتر قلق | يتصبّب عرقًا',
            'smug         | Smug                | smug knew told_you pleased right أخبرتك رضا | أخبرتك',
            'sigh         | Sigh                | sigh tired resigned again whatever تنهيدة تعب مجددًا | تنهيدة',
            'noidea       | No idea             | shrug dunno idk whatever unknown nobody لا فكرة كتفين | لا فكرة',
            'headdesk     | Head, meet desk     | headdesk facepalm ugh again despair رأس مكتب يأس | الرأس على المكتب',
            'wired        | Wired               | coffee caffeine awake jittery late قهوة كافيين سهر | مشحون بالقهوة',
            'shades       | Deal with it        | shades cool sunglasses smooth easy نظارة تدبر رائع | تدبّر أمرك',
            'sideeye      | Side eye            | side_eye sus suspicious doubt really شك ريبة نظرة | نظرة شك',
            'mischief     | Up to something     | mischief evil grin plotting trouble تخطيط مكر شر | أخطط لشيء',
            'smitten      | In love with it     | love adore heart_eyes beautiful nice حب إعجاب جميل | أحببتها',
            'hyped        | Hyped               | hyped excited stars amazed lets_go حماس متحمس | متحمّس',
            'screaming    | Screaming           | screaming aaah horror shock help صراخ رعب صدمة | صراخ',
            'afk          | Away                | afk away sleeping brb idle gone بعيد غائب عائد | بعيد عن الجهاز',
            'brain404     | Brain not found     | 404 brain empty blank tired lost دماغ فارغ تعب | الدماغ غير موجود',
            'partycaret   | Party caret         | party parrot celebrate dance shipped احتفال رقص محث | محث يرقص',
        ],
    ],
    /**
     * Stickers: the same idea at four times the size, and the size is the whole
     * of what makes them different.
     *
     * An emoji goes *in* a sentence. A sticker *is* the sentence — it is what
     * you send instead of typing, when the thing that happened is bigger than a
     * word and you do not want to find the word. So these are drawn on a canvas
     * with four times the area, they carry detail a 17-pixel glyph cannot hold,
     * and a message that is one of them and nothing else is drawn as a picture
     * rather than as a line of text.
     *
     * None of them restates one of the fifty-eight. `shipit` is "let us go" and
     * `shipped` is "it is out"; `greenbuild` is one build and `allgreen` is the
     * whole suite; `stare` is a look and `stuckhere` is an afternoon. Where a
     * sticker would only have been a bigger version of an emoji it is not here,
     * because the palette already had that picture and a second one at another
     * size is a choice nobody wants to make.
     *
     * `notfriday` is the one that is not a translation. The joke in English is
     * that nobody deploys on a Friday; the weekend it is about falls on Friday
     * and Saturday across the Arabic-speaking world, so the Arabic name says
     * Thursday. The drawing has no day in it for exactly that reason (compare
     * `shipfriday` above, which keeps Friday in both because it names the
     * English practice rather than the last day before a weekend).
     */
    [
        'Stickers',
        'ملصقات',
        [
            'shipped      | It is out           | shipped released live launched done crate خرجت نشرت وصلت | خرجت للناس',
            'allgreen     | Everything passing  | all green suite tests passing checks clean كله أخضر ناجح اختبارات | كل شيء أخضر',
            'itsalive     | It runs             | alive works running first_time lit يعمل حي يشتغل أخيرًا | إنه يعمل',
            'deadbug      | Bug squashed        | bug fixed dead squashed beetle solved علة حشرة أصلحت ماتت | ماتت العلة',
            'combedout    | Combed out          | rebase clean tidy history straight linear ترتيب تاريخ تنظيف مستقيم | رُتِّب التاريخ',
            'smallhours   | Three in the morning | late night 3am moon tired awake سهر ليل فجر متعب | الثالثة فجرًا',
            'stamped      | Approved            | approved stamp lgtm shipped review ok موافقة ختم مراجعة | مختومة بالموافقة',
            'dayone       | First commit        | first commit start beginning new repo أول التزام بداية | أول commit',
            'stuckhere    | Stuck               | stuck blocked spinning waiting lost عالق متوقف تائه | عالق هنا',
            'coffeeloop   | Another one         | coffee again loop refill more awake قهوة أخرى مجددًا | قهوة أخرى',
            'notfriday    | Not before a weekend | friday thursday weekend deploy wait no جمعة خميس نشر انتظر | ليس يوم الخميس',
            'welldone     | Well done           | well done nice congrats proud rosette أحسنت تهانينا فخور | أحسنت',
        ],
        true,
    ],
];
/** Every group of ours, in picker order. */
export const CUSTOM_GROUPS = TABLE.map(([name, nameAr, rows, sticker]) => ({
    name,
    nameAr,
    ...(sticker ? { sticker: true } : {}),
    emoji: rows.map((row) => {
        // "shipit | Ship it | ship deploy release شحن | اشحنها": code, English
        // name, the words it can be found by in either language, then the Arabic
        // name. A malformed row becomes an entry that can be seen and not found,
        // the same way emoji.ts treats one, rather than an exception at import
        // time on the Worker's hot path — and a row with no fourth column falls
        // back to the English name rather than to nothing, so a half-translated
        // roster still draws a tooltip.
        const [rawCode = row, rawName = row, rawKeys = '', rawAr = ''] = row.split('|').map((part) => part.trim());
        const code = rawCode.toLowerCase();
        return {
            c: `:${code}:`,
            code,
            name: rawName,
            nameAr: rawAr || rawName,
            // The shortcode first, always: the protocol test pins `keys[0]`, and a
            // thing has to be findable by the name it is typed with. The Arabic
            // words are appended to the same list, never in front of it.
            keys: [code, ...rawKeys.split(/\s+/).filter(Boolean)],
            // Carried down from the group rather than typed on every row: one place
            // says which drawer this is, and a row cannot disagree with the drawer
            // it is in.
            ...(sticker ? { sticker: true } : {}),
        };
    }),
}));
/** Every one of ours, flat. */
export const CUSTOM_EMOJI = CUSTOM_GROUPS.flatMap((g) => g.emoji);
/** Shortcode to entry. The one lookup the renderer needs, per token, per row. */
export const CUSTOM_BY_CODE = new Map(CUSTOM_EMOJI.map((e) => [e.code, e]));
/** The stickers, flat, in roster order. */
export const STICKERS = CUSTOM_EMOJI.filter((e) => e.sticker);
/**
 * Whether a shortcode names a sticker. The wire's question, asked by
 * `cleanReaction`: a sticker is a message and never a face on somebody else's
 * message, and the refusal has to happen in this package because the Worker is
 * what enforces it.
 */
export function isSticker(code) {
    return CUSTOM_BY_CODE.get(code.toLowerCase().replace(/^:+|:+$/g, ''))?.sticker === true;
}
/**
 * The sticker this message *is*, or null for a message that merely contains
 * one — or none.
 *
 * The renderer's question, and the reason it is a different question from
 * {@link gestureCount}. Counting is about how many pictures a wordless message
 * holds, so that a short run of them can be drawn large; this is about whether
 * the message has stopped being a line of text at all. A sticker with "nice"
 * typed after it is a sentence with a picture in it and is set as one; a
 * sticker on its own is the whole utterance and is drawn as a picture.
 *
 * Exactly one, and nothing else. Two stickers in a row are not a bigger
 * gesture, they are two pictures that would each want the full width of a
 * 280px panel — so a message like that falls back to the ordinary path and is
 * drawn at word size, which is legible and is what a run of emoji has always
 * done.
 */
export function loneSticker(text) {
    const trimmed = text.trim();
    // Cheap reject before the regex: the shortest sticker code plus its colons.
    if (trimmed.length < 3 || trimmed[0] !== ':' || trimmed[trimmed.length - 1] !== ':')
        return null;
    const single = /^:([a-z0-9_+-]{1,24}):$/i.exec(trimmed);
    if (!single)
        return null;
    const found = CUSTOM_BY_CODE.get(single[1].toLowerCase());
    return found?.sticker === true ? found : null;
}
/**
 * The token, as it appears in a message.
 *
 * Deliberately the same shape as `emoji.ts`'s shortcode — one to twenty-four
 * of the characters a shortcode is allowed — so that "is this ours?" is a map
 * lookup and never a second opinion about what a shortcode looks like.
 */
const TOKEN = /:([a-z0-9_+-]{1,24}):/gi;
/**
 * One message, cut into the runs of text and the emoji of ours between them.
 *
 * A message with none of ours in it — which is most of them — comes back as
 * the one part it went in as, so the common row still renders as a single
 * string and the list is not paying a React element per word.
 *
 * Anything that looks like a shortcode and is not one of ours is left inside
 * the text, untouched and unrendered. `:wq` survives, and so does the person
 * pasting a Python slice.
 */
export function splitCustom(text) {
    TOKEN.lastIndex = 0;
    const parts = [];
    let at = 0;
    for (let m = TOKEN.exec(text); m !== null; m = TOKEN.exec(text)) {
        const found = CUSTOM_BY_CODE.get(m[1].toLowerCase());
        if (!found)
            continue;
        if (m.index > at)
            parts.push({ text: text.slice(at, m.index) });
        parts.push({ emoji: found });
        at = m.index + m[0].length;
    }
    if (parts.length === 0)
        return [{ text }];
    if (at < text.length)
        parts.push({ text: text.slice(at) });
    return parts;
}
/**
 * How many emoji, if the message is nothing but emoji — ours counted with
 * Unicode's.
 *
 * The list draws a short run of them large (see `emojiOnly`, which this
 * extends), and a reply of `:shipit::tada:` is exactly as much of a gesture as
 * a reply of 🎉. Counting ours separately would have made the one kind of
 * emoji this product invented the one kind that never gets to be a gesture.
 */
export function gestureCount(text) {
    const trimmed = text.trim();
    if (trimmed === '')
        return 0;
    let ours = 0;
    const rest = trimmed.replace(TOKEN, (whole, code) => {
        if (!CUSTOM_BY_CODE.has(code.toLowerCase()))
            return whole;
        ours += 1;
        // A space, not nothing: "🎉:shipit:🎉" must not become one sequence.
        return ' ';
    });
    if (ours === 0)
        return emojiOnly(trimmed);
    const unicode = emojiOnly(rest);
    // Words left over between the pictures. Then it is a sentence, and a
    // sentence is set in body copy however it is punctuated.
    if (unicode === 0 && rest.trim() !== '')
        return 0;
    return ours + unicode;
}
/** What the palette offers for a search, in roster order. Empty query, all of them. */
export function searchCustom(query) {
    const q = query.trim().toLowerCase().replace(/^:+|:+$/g, '');
    if (q === '')
        return CUSTOM_EMOJI;
    const starts = CUSTOM_EMOJI.filter((e) => e.keys.some((k) => k.startsWith(q)));
    if (starts.length > 0)
        return starts;
    return CUSTOM_EMOJI.filter((e) => e.keys.some((k) => k.includes(q)) || e.name.toLowerCase().includes(q));
}
//# sourceMappingURL=custom.js.map